import React, { useState } from 'react';
import { AppBar, Toolbar, Container, Button, Box, IconButton, Drawer, List, ListItem, ListItemText } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/FenceSketch-Logo.png';

const Header = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const menuItems = [
    { label: 'Features', sectionId: 'features' },
    { label: 'Pricing', sectionId: 'pricing' },
    { label: 'About', sectionId: 'about' },
    { label: 'Contact', sectionId: 'contact' }
  ];

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMobileMenuOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={1}
        sx={{
          bgcolor: 'white',
          color: theme.palette.typography.heading.primary,
          borderBottom: `1px solid ${theme.palette.borders.border}`
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            {/* Logo */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                gap: 1.5
              }}
              onClick={() => handleNavigation('/')}
            >
              <img 
                src={logo} 
                alt="Fence Sketch Logo" 
                style={{ height: '40px', width: 'auto' }}
              />
              <Box
                sx={{
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  color: theme.palette.brand.brand,
                  lineHeight: 1
                }}
              >
                Fence Sketch
              </Box>
            </Box>

            {/* Desktop Navigation */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 3
              }}
            >
              {menuItems.map((item) => (
                <Button
                  key={item.label}
                  onClick={() => scrollToSection(item.sectionId)}
                  sx={{
                    color: theme.palette.typography.heading.primary,
                    fontWeight: 500,
                    fontSize: '1rem',
                    textTransform: 'none',
                    '&:hover': {
                      color: theme.palette.brand.brand,
                      bgcolor: 'transparent'
                    }
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>

            {/* Desktop Auth Buttons */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                gap: 2,
                alignItems: 'center'
              }}
            >
              <Button
                variant="text"
                component="a"
                href="https://portal.fencesketch.com/login"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.typography.heading.primary,
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  '&:hover': {
                    color: theme.palette.brand.brand,
                    bgcolor: 'transparent'
                  }
                }}
              >
                Sign In
              </Button>
              <Button
                variant="contained"
                component="a"
                href="https://portal.fencesketch.com/register"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  px: 3
                }}
              >
                Sign Up
              </Button>
            </Box>

            {/* Mobile Menu Button */}
            <IconButton
              sx={{
                display: { xs: 'flex', md: 'none' },
                color: theme.palette.typography.heading.primary
              }}
              onClick={handleMobileMenuToggle}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        sx={{
          display: { xs: 'block', md: 'none' }
        }}
      >
        <Box
          sx={{
            width: 280,
            height: '100%',
            bgcolor: 'white'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: `1px solid ${theme.palette.borders.border}`
            }}
          >
            <Box
              sx={{
                fontWeight: 700,
                fontSize: '1.25rem',
                color: theme.palette.brand.brand
              }}
            >
              Menu
            </Box>
            <IconButton onClick={handleMobileMenuToggle}>
              <CloseIcon />
            </IconButton>
          </Box>

          <List sx={{ pt: 2 }}>
            {menuItems.map((item) => (
              <ListItem
                button
                key={item.label}
                onClick={() => scrollToSection(item.sectionId)}
                sx={{
                  py: 1.5,
                  '&:hover': {
                    bgcolor: theme.palette.surfaces.surface.hover
                  }
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '1rem',
                    fontWeight: 500
                  }}
                />
              </ListItem>
            ))}
          </List>

          <Box sx={{ p: 2, mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              component="a"
              href="https://portal.fencesketch.com/login"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ py: 1.5, fontSize: '1rem', fontWeight: 600 }}
            >
              Sign In
            </Button>
            <Button
              variant="contained"
              fullWidth
              component="a"
              href="https://portal.fencesketch.com/register"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ py: 1.5, fontSize: '1rem', fontWeight: 600 }}
            >
              Sign Up
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Header;

